import type { NotificationPopup as NotificationPopupType } from 'codegen/types'
import * as Dialog from 'primitives/dialog'
import * as React from 'react'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useLocalStorage } from 'hooks/use-local-storage'
import { Link } from '../link'
import { Countdown } from '../countdown'
import { useUserId } from '@repo/cf-user/hooks/use-user-data'

const SHOW_INTERVAL = 1000 * 60 * 60 * 4 //4 hours
const LOCAL_STORAGE_KEY = 'hasSeenSitewidePopup'

export function NotificationPopup({ popup }: { popup: NotificationPopupType }) {
  const { text, textColor, backgroundColor, closeText } = popup

  const [isOpen, setIsOpen] = React.useState(false)
  // eslint-disable-next-line cf-web/codegen-query-patterns
  const isUserLoggedIn = useIsLoggedInQuery()
  const userId = useUserId()
  const localStorageKey = `${LOCAL_STORAGE_KEY}.${isUserLoggedIn.data ? userId : 'guest'}`
  const [hasSeenSitewidePopup, setHasSeenSitewidePopup] = useLocalStorage<string | null>(
    localStorageKey,
    null,
  )

  React.useEffect(() => {
    if (window === undefined) {
      return
    }

    try {
      if (!hasSeenSitewidePopup) {
        setHasSeenSitewidePopup(Date.now().toString())
        setIsOpen(true)

        return
      }

      const dateNow = Date.now()

      if (Number(dateNow) - Number(hasSeenSitewidePopup) >= SHOW_INTERVAL) {
        setHasSeenSitewidePopup(dateNow.toString())
        setIsOpen(true)

        return
      }

      setIsOpen(false)
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false)
    }
  }

  const renderStripe = () => {
    const { title, button } = popup

    if (!title || !button) {
      return null
    }

    const { backgroundColor: stripeColor } = button

    return (
      <div className="flex w-full items-center justify-center">
        <div
          style={{ '--stripe-background-color': stripeColor } as React.CSSProperties}
          className="flex h-[10px] w-full justify-center bg-[var(--stripe-background-color)]"
        >
          <p
            className="px-1 text-[12px] font-bold uppercase leading-[12px] tracking-[0.9px]"
            style={{ color: textColor, backgroundColor: backgroundColor }}
          >
            {title}
          </p>
        </div>
      </div>
    )
  }

  const renderCountdown = () => {
    const { countdown } = popup

    if (!countdown) {
      return null
    }

    const { text: countDownPrefix, value: date } = countdown

    return (
      <Countdown
        className="mb-5 mt-[10px]"
        date={date}
        prefix={countDownPrefix}
        separator={' : '}
        style={{ color: textColor }}
      />
    )
  }

  const renderButton = () => {
    const { button } = popup

    if (!button) {
      return null
    }

    const {
      link: buttonHref,
      text: buttonText,
      textColor: buttonTextColor,
      hoverTextColor: buttonTextHoverColor,
      backgroundColor: buttonBackgroundColor,
      hoverBackgroundColor: buttonBackgroundHoverColor,
    } = button

    return (
      <Link
        href={buttonHref}
        style={
          {
            '--button-text-color': buttonTextColor,
            '--button-text-hover-color': buttonTextHoverColor,
            '--button-background-color': buttonBackgroundColor,
            '--button-background-hover-color': buttonBackgroundHoverColor,
          } as React.CSSProperties
        }
        className="text-12 m-auto mb-5 flex h-[40px] w-full max-w-[220px] items-center justify-center rounded bg-[var(--button-background-color)] font-bold uppercase tracking-[0.9px] text-[var(--button-text-color)] hover:bg-[var(--button-background-hover-color)] hover:text-[var(--button-text-hover-color)]"
      >
        {buttonText}
      </Link>
    )
  }

  return (
    <div className="relative z-[1300]">
      <Dialog.Root open={isOpen} onOpenChange={handleOnOpenChange}>
        <Dialog.Body dismissable={false}>
          <div
            className="rounded-2 m-auto flex w-full max-w-[400px] flex-col bg-[var(--background-color)] p-5 text-center text-[var(--text-color)]"
            style={
              {
                '--text-color': textColor,
                '--background-color': backgroundColor,
              } as React.CSSProperties
            }
          >
            {renderStripe()}
            <div
              className="text-14 flex flex-col gap-[10px] pb-5 pt-7 [&>h1]:text-[36px] [&>h1]:font-bold [&>h2]:text-[28px] [&>h2]:font-bold [&>h2]:leading-[30px] [&>h3]:text-[16px] [&>h3]:font-bold [&>h3]:leading-[24px]"
              dangerouslySetInnerHTML={{ __html: text }}
            />
            {renderCountdown()}
            {renderButton()}
            <button
              onClick={() => setIsOpen(false)}
              className="text-12 m-auto underline outline-none"
            >
              {closeText}
            </button>
          </div>
        </Dialog.Body>
      </Dialog.Root>
    </div>
  )
}
